// frontend/src/components/Modals/ChannelHistoryModal/ChannelHistoryModal.js

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from '../../../config';
import './ChannelHistoryModal.css';

const ChannelHistoryModal = ({ isOpen, onClose, channelId, channelName, BASE_URL }) => {
  const [historyData, setHistoryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('info'); // 'info' veya 'logs'
  const [activityLogs, setActivityLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Modal açıldığında veriyi yükle
  useEffect(() => {
    if (isOpen && channelId) {
      fetchChannelHistory();
    }
  }, [isOpen, channelId]);

  // Tab değiştiğinde logları yükle
  useEffect(() => {
    if (isOpen && channelId && activeTab === 'logs') {
      fetchActivityLogs();
    }
  }, [isOpen, channelId, activeTab, currentPage]);

  // Modal açıldığında state'leri sıfırla
  useEffect(() => {
    if (isOpen) {
      setActiveTab('info');
      setCurrentPage(1);
      setActivityLogs([]);
    }
  }, [isOpen]);

  const fetchChannelHistory = async () => {
    setLoading(true);
    setHistoryData(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/channels/${channelId}/logs?page=1&limit=1`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.data.success) {
        setHistoryData(response.data.data);
      } else {
        console.error('Veri yükleme başarısız:', response.data.error);
      }
    } catch (error) {
      console.error('Kanal bilgileri yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchActivityLogs = async () => {
    setLogsLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/channels/${channelId}/logs?page=${currentPage}&limit=20`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.data.success) {
        const data = response.data.data;
        setActivityLogs(data.logs || []);
        setTotalPages(data.pagination?.total_pages || 1);
      } else {
        console.error('Log yükleme başarısız:', response.data.error);
        setActivityLogs([]);
      }
    } catch (error) {
      console.error('İşlem geçmişi yüklenemedi:', error);
      setActivityLogs([]);
    } finally {
      setLogsLoading(false);
    }
  };

  const formatDate = (dateString, alwaysShowFullDate = false) => {
    if (!dateString) return 'Henüz yok';
    
    try {
      let date;
      
      if (dateString.includes('Z') || dateString.includes('+')) {
        date = new Date(dateString);
      } else {
        date = new Date(dateString.replace(' ', 'T'));
      }
      
      if (isNaN(date.getTime())) {
        console.error('Geçersiz tarih:', dateString);
        return 'Geçersiz tarih';
      }
      
      const options = {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        weekday: 'long'
      };
      
      const fullDate = date.toLocaleDateString('tr-TR', options);
      
      if (alwaysShowFullDate) {
        return fullDate;
      }
      
      const now = new Date();
      const diffTime = now - date;
      const diffSeconds = Math.floor(Math.abs(diffTime) / 1000);
      const diffMinutes = Math.floor(Math.abs(diffTime) / (1000 * 60));
      const diffHours = Math.floor(Math.abs(diffTime) / (1000 * 60 * 60));
      const diffDays = Math.floor(Math.abs(diffTime) / (1000 * 60 * 60 * 24));
      
      let relativeTime = '';
      
      if (diffTime < -60000) {
        return fullDate + ' (Gelecek tarih?)';
      }
      
      if (diffSeconds < 60) {
        relativeTime = diffSeconds < 5 ? 'Şimdi' : `${diffSeconds} saniye önce`;
      } else if (diffMinutes < 60) {
        relativeTime = diffMinutes === 1 ? '1 dakika önce' : `${diffMinutes} dakika önce`;
      } else if (diffHours < 24) {
        relativeTime = diffHours === 1 ? '1 saat önce' : `${diffHours} saat önce`;
      } else if (diffDays === 1) {
        relativeTime = 'Dün';
      } else if (diffDays < 7) {
        relativeTime = `${diffDays} gün önce`;
      } else if (diffDays < 30) {
        const weeks = Math.floor(diffDays / 7);
        relativeTime = weeks === 1 ? '1 hafta önce' : `${weeks} hafta önce`;
      } else if (diffDays < 365) {
        const months = Math.floor(diffDays / 30);
        relativeTime = months === 1 ? '1 ay önce' : `${months} ay önce`;
      } else {
        const years = Math.floor(diffDays / 365);
        relativeTime = years === 1 ? '1 yıl önce' : `${years} yıl önce`;
      }
      
      const timeOnly = date.toLocaleTimeString('tr-TR', { 
        hour: '2-digit', 
        minute: '2-digit'
      });
      
      const dateOnly = date.toLocaleDateString('tr-TR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
      
      const today = new Date();
      const isToday = date.getDate() === today.getDate() && 
                     date.getMonth() === today.getMonth() && 
                     date.getFullYear() === today.getFullYear();
      
      if (isToday) {
        return `Bugün ${timeOnly} (${relativeTime})`;
      } else if (diffDays === 1) {
        return `Dün ${timeOnly}`;
      } else if (diffDays < 7) {
        return `${dateOnly} ${timeOnly} (${relativeTime})`;
      } else {
        return `${dateOnly} ${timeOnly}`;
      }
      
    } catch (error) {
      console.error('Tarih formatlama hatası:', error, 'Tarih:', dateString);
      return 'Hatalı tarih';
    }
  };

  const getLogIcon = (type) => {
    const icons = {
      'post_sent': { icon: '📤', color: '#10b981' },
      'post_failed': { icon: '❌', color: '#ef4444' },
      'post_scheduled': { icon: '⏰', color: '#3b82f6' },
      'channel_created': { icon: '🎉', color: '#8b5cf6' },
      'channel_updated': { icon: '✏️', color: '#f59e0b' },
      'channel_info_updated': { icon: '🔄', color: '#06b6d4' },
      'channel_photo_updated': { icon: '📸', color: '#ec4899' },
      'channel_telegram_updated': { icon: '🔗', color: '#6366f1' },
      'stats_update': { icon: '📊', color: '#06b6d4' },
      'activity': { icon: '📝', color: '#6366f1' },
      'system': { icon: '⚙️', color: '#6b7280' },
      'CHANNEL_ADDED': { icon: '🎉', color: '#8b5cf6' },
      'CHANNEL_UPDATED': { icon: '✏️', color: '#f59e0b' },
      'CHANNEL_DELETED': { icon: '🗑️', color: '#ef4444' }
    };
    
    return icons[type] || { icon: '📋', color: '#6b7280' };
  };

  const getStatusBadge = (status) => {
    const badges = {
      'success': '✅',
      'failed': '❌',
      'pending': '⏳',
      'sent': '✅',
      'scheduled': '⏰'
    };
    
    return badges[status] || '⏳';
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="history-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>📊 Kanal Bilgileri</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Bilgiler yükleniyor...</p>
          </div>
        ) : historyData ? (
          <>
            <div className="modal-subheader">
              <h3>{channelName || 'Kanal'}</h3>
            </div>

            <div className="tabs">
              <button 
                className={`tab ${activeTab === 'info' ? 'active' : ''}`}
                onClick={() => setActiveTab('info')}
                type="button"
              >
                📋 Genel Bilgiler
              </button>
              <button 
                className={`tab ${activeTab === 'logs' ? 'active' : ''}`}
                onClick={() => setActiveTab('logs')}
                type="button"
              >
                📜 İşlem Geçmişi
              </button>
            </div>

            <div className="modal-body">
              {activeTab === 'info' ? (
                <div className="info-content">
                  <div className="info-section">
                    <h4>📡 Kanal Bilgileri</h4>
                    <div className="info-grid">
                      <div className="info-item">
                        <span className="label">Kanal Adı:</span>
                        <span className="value">{historyData.channel_info?.name}</span>
                      </div>
                      <div className="info-item">
                        <span className="label">Kullanıcı Adı:</span>
                        <span className="value">
                          {historyData.channel_info?.username ? `@${historyData.channel_info.username}` : 'Yok'}
                        </span>
                      </div>
                      <div className="info-item">
                        <span className="label">Chat ID:</span>
                        <span className="value" style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                          {historyData.channel_info?.chat_id}
                        </span>
                      </div>
                      <div className="info-item">
                        <span className="label">Tür:</span>
                        <span className="value">
                          {historyData.channel_info?.type === 'channel' ? '📢 Kanal' : '👥 Grup'}
                        </span>
                      </div>
                    </div>
                    {historyData.channel_info?.description && (
                      <div style={{ marginTop: '16px' }}>
                        <span className="label">Açıklama:</span>
                        <p style={{ 
                          marginTop: '8px', 
                          padding: '12px', 
                          background: 'white', 
                          borderRadius: '6px',
                          fontSize: '13px',
                          lineHeight: '1.5',
                          color: '#4b5563'
                        }}>
                          {historyData.channel_info.description}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="info-section">
                    <h4>👤 Oluşturma Bilgileri</h4>
                    <div className="info-grid">
                      <div className="info-item">
                        <span className="label">Oluşturan:</span>
                        <span className="value">{historyData.channel_info?.created_by || 'Sistem'}</span>
                      </div>
                      <div className="info-item">
                        <span className="label">Oluşturma Tarihi:</span>
                        <span className="value">{formatDate(historyData.channel_info?.created_at, true)}</span>
                      </div>
                    </div>
                  </div>

                  {historyData.channel_info?.updated_at && historyData.channel_info?.updated_at !== historyData.channel_info?.created_at && (
                    <div className="info-section">
                      <h4>✏️ Son Güncelleme</h4>
                      <div className="info-grid">
                        <div className="info-item">
                          <span className="label">Güncelleme Tarihi:</span>
                          <span className="value">{formatDate(historyData.channel_info?.updated_at, true)}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {historyData.recent_stats && historyData.recent_stats.length > 0 && (
                    <div className="info-section">
                      <h4>📈 Son 7 Günlük İstatistikler</h4>
                      <div className="recent-stats-grid">
                        {historyData.recent_stats.map((stat, index) => (
                          <div key={index} className="recent-stat-item">
                            <div className="stat-date">
                              {new Date(stat.date).toLocaleDateString('tr-TR', { 
                                day: 'numeric', 
                                month: 'short' 
                              })}
                            </div>
                            <div className="stat-values">
                              <span>👥 {stat.member_count?.toLocaleString('tr-TR') || 0}</span>
                              <span>🟢 {stat.online_count?.toLocaleString('tr-TR') || 0}</span>
                              {stat.post_count > 0 && <span>📝 {stat.post_count}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="logs-content">
                  {logsLoading ? (
                    <div className="logs-loading">
                      <div className="spinner"></div>
                      <p>İşlem geçmişi yükleniyor...</p>
                    </div>
                  ) : activityLogs.length > 0 ? (
                    <>
                      <div className="logs-timeline">
                        {activityLogs.map((log, index) => {
                          const logStyle = getLogIcon(log.type);
                          return (
                            <div key={log.id || index} className={`log-item ${log.status || ''}`}>
                              <div 
                                className="log-icon" 
                                style={{ 
                                  backgroundColor: logStyle.color + '20', 
                                  color: logStyle.color 
                                }}
                              >
                                <span>{logStyle.icon}</span>
                              </div>
                              <div className="log-content">
                                <div className="log-header">
                                  <h4>{log.message}</h4>
                                  <span className="log-time">{formatDate(log.created_at)}</span>
                                </div>
                                {log.content_title && (
                                  <p className="log-details">
                                    📝 İçerik: {log.content_title}
                                  </p>
                                )}
                                {log.error_message && (
                                  <p className="log-error">
                                    ⚠️ Hata: {log.error_message}
                                  </p>
                                )}
                                {log.username && (
                                  <div className="log-footer">
                                    <span className="log-user">👤 {log.username}</span>
                                  </div>
                                )}
                                {log.status && (
                                  <div className="log-status">
                                    <span>{getStatusBadge(log.status)}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      
                      {totalPages > 1 && (
                        <div className="pagination-container">
                          <button 
                            className="pagination-btn"
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={currentPage === 1}
                          >
                            ← Önceki
                          </button>
                          <span className="pagination-info">
                            Sayfa {currentPage} / {totalPages}
                          </span>
                          <button 
                            className="pagination-btn"
                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                            disabled={currentPage === totalPages}
                          >
                            Sonraki →
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="empty-logs">
                      <p>📭 Henüz işlem geçmişi bulunmuyor</p>
                      <p style={{ fontSize: '12px', color: '#999', marginTop: '10px' }}>
                        Kanal işlemleri yapıldığında burada görünecek.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button onClick={onClose} className="btn-close">
                Kapat
              </button>
            </div>
          </>
        ) : (
          <div className="error-state">
            <p>❌ Bilgiler yüklenemedi</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChannelHistoryModal;